/* LegalAliases — lets a creator link every name they use professionally
 * (stage names, legal songwriter names, publishing entity names, etc.) so
 * RoyAI's Proactive Audit Engine searches MusicBrainz/ISRC scans across
 * all of them simultaneously.
 */
import { useEffect, useState } from "react";
import { Users, Plus, X, Info } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function LegalAliases() {
  const { user, refresh } = useAuth();
  const [aliases, setAliases] = useState([]);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setAliases(Array.isArray(user?.legal_aliases) ? user.legal_aliases : []);
  }, [user?.legal_aliases]);

  async function persist(next) {
    setSaving(true);
    try {
      await api.post("/profile", { legal_aliases: next });
      await refresh?.();
      toast.success("Legal aliases saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  }

  function addDraft() {
    const v = draft.trim();
    if (!v) return;
    if (aliases.length >= 8) { toast.error("Up to 8 aliases."); return; }
    if (aliases.some((a) => a.toLowerCase() === v.toLowerCase())) {
      toast.error("Already added."); return;
    }
    const next = [...aliases, v];
    setAliases(next);
    setDraft("");
    persist(next);
  }

  function removeAt(i) {
    const next = aliases.filter((_, idx) => idx !== i);
    setAliases(next);
    persist(next);
  }

  return (
    <section className="border border-white/10 bg-[#0A0A0A] p-6 lg:p-8" data-testid="legal-aliases-card">
      <div className="flex items-start gap-3 mb-2">
        <Users size={18} className="text-amber-400 shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="text-overline mb-1">Identity</div>
          <h2 className="font-display text-2xl font-medium tracking-tight">Legal Aliases</h2>
        </div>
      </div>
      <p className="text-sm text-neutral-400 mb-6 max-w-2xl">
        Add every name you release under. Producer stage name, legal songwriter name, publishing entity name — anything. When RoyAI audits your catalog on MusicBrainz and ISRC databases, it will search <span className="text-amber-400">every</span> identity so nothing gets missed.
      </p>

      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addDraft(); } }}
          placeholder="e.g. Marvin E Haskins  ·  The Martian SMG  ·  Only Few Get Selected"
          maxLength={80}
          disabled={saving}
          className="flex-1 bg-black border border-white/10 focus:border-amber-500 outline-none px-4 py-3 text-sm text-white placeholder:text-neutral-600 font-mono-tech"
          data-testid="legal-alias-input"
        />
        <button
          type="button"
          onClick={addDraft}
          disabled={saving || !draft.trim()}
          className="bg-amber-500 text-black px-5 py-3 text-xs font-mono-tech uppercase tracking-[0.22em] font-semibold hover:brightness-110 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          data-testid="legal-alias-add"
        >
          <Plus size={14} /> Add alias
        </button>
      </div>

      {aliases.length > 0 ? (
        <ul className="flex flex-wrap gap-2 mb-3" data-testid="legal-alias-list">
          {aliases.map((a, i) => (
            <li key={`${a}-${i}`}
                className="inline-flex items-center gap-2 border border-amber-500/30 bg-amber-500/[0.06] px-3 py-1.5"
                data-testid={`legal-alias-chip-${i}`}>
              <span className="font-mono-tech text-xs text-white">{a}</span>
              <button
                type="button"
                onClick={() => removeAt(i)}
                disabled={saving}
                aria-label={`Remove ${a}`}
                className="text-amber-400 hover:text-rose-400 transition-colors"
                data-testid={`legal-alias-remove-${i}`}
              >
                <X size={12} />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <div className="text-xs text-neutral-500 border border-dashed border-white/10 px-4 py-3 mb-3">
          No aliases added yet. Your BeatCredsi Passport name is used as the default identity.
        </div>
      )}

      <div className="flex items-start gap-2 text-[11px] text-neutral-500">
        <Info size={12} className="mt-0.5 shrink-0 text-amber-400/70" />
        <p>
          Cross-alias matches — tracks found under two of your identities — are auto-flagged as <span className="text-emerald-400">Verified Creations</span> on your Passport when RoyAI runs the next audit.
        </p>
      </div>
    </section>
  );
}
